import { Button, Icon } from "@aidkitorg/component-library";
import { SupportedLanguage } from "@aidkitorg/i18n/lib";
import {
  ArrowsRightLeftIcon,
  CreditCardIcon,
  FaceSmileIcon,
  IdentificationIcon,
  LanguageIcon,
  WifiIcon,
} from "@heroicons/react/24/outline";
import { useContext } from "react";
import InterfaceContext, { ConfigurationContext } from "../../Context";
import { langToWord, useLocalizedStrings } from "../../Localization";
import { AidKitLogo, SpacedSpinner } from "../../Util";
import { Dropdown } from "../../Components/Dropdown";
import type { LivenessDetection } from "./SessionUIController/types";

const previewStepIconClass = "h-12 w-12 shrink-0 text-gray-700";

export type LivenessPreludeExperimentalProps = {
  livenessConfig: LivenessDetection | null;
  L: ReturnType<typeof useLocalizedStrings>;
  setReady: (ready: boolean) => void;
};

/**
 * Experimental liveness identity prelude (design-system list + primary CTA).
 */
export function LivenessPreludeExperimental(props: LivenessPreludeExperimentalProps) {
  const { livenessConfig, L, setReady } = props;
  const context = useContext(InterfaceContext);
  const config = useContext(ConfigurationContext);

  const previewSteps = livenessConfig
    ? (
      [
        {
          key: "front" as const,
          label: L.questions.identity.step_preview_id_front,
          icon: <IdentificationIcon className={previewStepIconClass} aria-hidden />,
        },
        ...(livenessConfig.identification
          ? [
            {
              key: "back" as const,
              label:
                    livenessConfig.identification.back.alternativeDocument?.kind === "Debit Card"
                      ? L.questions.identity.step_preview_debit_card
                      : L.questions.identity.step_preview_id_back,
              icon:
                    livenessConfig.identification.back.alternativeDocument?.kind === "Debit Card" ? (
                      <CreditCardIcon className={previewStepIconClass} aria-hidden />
                    ) : (
                      <ArrowsRightLeftIcon className={previewStepIconClass} aria-hidden />
                    ),
            },
          ]
          : []),
        {
          key: "selfie" as const,
          label: L.questions.identity.step_preview_selfie,
          icon: <FaceSmileIcon className={previewStepIconClass} aria-hidden />,
        },
      ]
    ).filter((step) => (livenessConfig.identification ? true : step.key === "selfie"))
    : [];

  const stepCount = previewSteps.length;
  const previewIntro =
    stepCount > 1
      ? L.questions.identity.preview_instructions_multi
      : L.questions.identity.preview_instructions_one_step;

  return (
    <div className="flex min-h-full w-full flex-col bg-gray-100">
      <div className="w-full border-b border-gray-200 bg-white">
        <div className="mx-auto w-full max-w-lg px-6 py-8">
          <div className="mb-4 flex flex-row items-center justify-between gap-3">
            <AidKitLogo width={100} height={50} />
            <div className="shrink-0 text-right">
              <Dropdown
                description="Language"
                label={
                  <>
                    <LanguageIcon />
                    <span className="ml-1 md:block">
                      {langToWord(context.lang as SupportedLanguage)}
                    </span>
                  </>
                }
                options={(config.languages?.split(",") || []).map((l: string) => ({
                  label: langToWord(l as SupportedLanguage),
                  callback: () => {
                    context.setLanguage(l as SupportedLanguage);
                  },
                }))}
              />
            </div>
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {L.questions.identity.title}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">{L.questions.identity.verification_headline}</h1>
        </div>
      </div>
      {livenessConfig ? (
        <div className="w-full flex-1 bg-white">
          <div className="mx-auto flex w-full max-w-lg flex-col px-6 pb-8 pt-2">
            <div className="my-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="flex items-center gap-2 font-semibold text-amber-700">
                <WifiIcon className="h-5 w-5 shrink-0" aria-hidden />
                {L.liveness.wifi_title}
              </p>
              <p className="mt-1 text-sm text-amber-800">{L.liveness.wifi_body}</p>
            </div>
            <p className="mb-2 text-base text-gray-800">{previewIntro}</p>
            <ul role="list" className="mb-8 space-y-3">
              {previewSteps.map((step, i) => (
                <li
                  key={step.key}
                  className="flex items-center gap-3 rounded-lg border border-gray-200/80 bg-gray-100 p-3"
                >
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--ak-theme-color-button-primary-brand-background, #4338ca)",
                      color: "var(--ak-theme-color-button-primary-brand-text, #ffffff)",
                    }}
                    aria-hidden
                  >
                    {i + 1}
                  </div>
                  <span className="flex w-[90px] shrink-0 items-center justify-center" aria-hidden>
                    {step.icon}
                  </span>
                  <span className="min-w-0 flex-1 font-medium text-gray-900">{step.label}</span>
                </li>
              ))}
            </ul>
            <Button
              variant="primary"
              size="lg"
              className="w-full justify-center"
              iconPlacement="right"
              icon={
                <Icon
                  name="ArrowRightIcon"
                  type="solid"
                  role="decorative"
                  size="sm"
                  className="!h-4 !w-4"
                />
              }
              onClick={() => {
                setReady(true);
              }}
            >
              {L.questions.identity.begin_identity_verification}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center py-12">
          <SpacedSpinner />
        </div>
      )}
    </div>
  );
}
